<template>
  <div class="modal fade dq-leave-type-modal" id="leave_type_modal" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-md dq-leave-type-dialog">
      <div class="modal-content dq-modal-content" :key="modalKey">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-indigo">
              <i class="ti ti-clipboard-check"></i>
            </div>
            <div>
              <h4 class="modal-title dq-title mb-0">
                {{ isEdit ? "Edit Leave Type" : "Create Leave Type" }}
              </h4>
              <div class="dq-subtitle">
                Define rules for accrual, carry forward, and paid/unpaid behavior.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submit">
          <div class="modal-body dq-body pb-0">
            <!-- Quick Setup -->
            <div class="dq-section">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-badge dq-badge-indigo">
                    <i class="ti ti-bolt"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Quick setup</div>
                    <div class="dq-help">
                      Choose a common leave type or select <b>Custom</b> to type your own.
                    </div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="mb-3">
                  <label class="form-label dq-label">
                    Name <span class="text-danger">*</span>
                  </label>
                  <select
                    v-model="nameSelection"
                    class="form-select dq-input"
                    required
                    @change="onNameSelect"
                  >
                    <option value="">-- Select or choose Custom --</option>
                    <option v-for="s in suggestions" :key="s.code" :value="s.name">
                      {{ s.name }}
                    </option>
                    <option value="__custom__">Custom (Type your own)</option>
                  </select>
                </div>

                <div class="mb-3" v-if="nameSelection === '__custom__'">
                  <label class="form-label dq-label">
                    Custom Name <span class="text-danger">*</span>
                  </label>
                  <input
                    v-model.trim="form.name"
                    class="form-control dq-input"
                    placeholder="e.g., Study Leave"
                    required
                  />
                </div>
              </div>
            </div>

            <!-- Core Details -->
            <div class="dq-section mt-3">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-badge dq-badge-blue">
                    <i class="ti ti-settings"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Core details</div>
                    <div class="dq-help">These values drive entitlement calculations.</div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="mb-3">
                  <label class="form-label dq-label">
                    Code <span class="text-danger">*</span>
                  </label>

                  <div class="input-group dq-input-group">
                    <span class="input-group-text dq-prefix">
                      <i class="ti ti-hash"></i>
                    </span>
                    <input
                      v-model.trim="form.code"
                      class="form-control dq-input"
                      placeholder="e.g., ANNUAL"
                      required
                    />
                  </div>

                  <div class="dq-help mt-2">
                    Short unique identifier (e.g., <b>ANNUAL</b>, <b>SICK</b>, <b>MAT</b>).
                  </div>
                </div>

                <div class="row g-3">
                  <div class="col-md-6">
                    <div class="mb-2">
                      <label class="form-label dq-label">
                        Default Days / Year <span class="text-danger">*</span>
                      </label>
                      <input
                        v-model.number="form.default_days"
                        type="number"
                        min="0"
                        class="form-control dq-input"
                        placeholder="e.g., 21"
                        required
                      />
                      <div class="dq-help mt-2">
                        This is the yearly entitlement for this leave type.
                      </div>
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div class="dq-switch-card">
                      <div class="dq-switch-title">
                        <i class="ti ti-cash me-1"></i> Paid
                      </div>
                      <div class="form-check form-switch dq-switch">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          v-model="form.paid"
                          id="dq_paid_switch"
                        />
                        <label class="form-check-label" for="dq_paid_switch">
                          {{ form.paid ? "Paid" : "Unpaid (LOP)" }}
                        </label>
                      </div>
                      <div class="dq-help">
                        Unpaid leave can impact payroll calculations depending on settings.
                      </div>
                    </div>
                  </div>
                </div>

                <div class="row g-3 mt-1">
                  <div class="col-md-6">
                    <label class="form-label dq-label">Eligibility</label>
                    <select v-model="form.eligibility" class="form-select dq-input">
                      <option value="all">All Employees</option>
                      <option value="male_only">Male Only</option>
                      <option value="female_only">Female Only</option>
                      <option value="none">Hidden / Not Applicable</option>
                    </select>
                  </div>

                  <div class="col-md-6">
                    <div class="dq-switch-card">
                      <div class="dq-switch-title">
                        <i class="ti ti-user-circle me-1"></i> Visible To Employees
                      </div>
                      <div class="form-check form-switch dq-switch">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          v-model="form.visible_in_employee"
                          id="dq_visible_employee_switch"
                        />
                        <label class="form-check-label" for="dq_visible_employee_switch">
                          {{ form.visible_in_employee ? "Visible" : "Hidden" }}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Carry Forward -->
            <div class="dq-section mt-3">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-badge dq-badge-green">
                    <i class="ti ti-refresh"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Carry forward</div>
                    <div class="dq-help">Allow unused days to roll into a new period.</div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="dq-switch-card dq-switch-card-soft">
                  <div class="dq-switch-title">
                    <i class="ti ti-calendar-plus me-1"></i> Carry forward
                  </div>
                  <div class="form-check form-switch dq-switch">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      v-model="form.carry_forward"
                      id="dq_cf_switch"
                    />
                    <label class="form-check-label" for="dq_cf_switch">
                      {{ form.carry_forward ? "Enabled" : "Disabled" }}
                    </label>
                  </div>
                  <div class="dq-help">
                    If enabled, you can optionally cap the maximum carry forward days.
                  </div>
                </div>

                <div class="mt-3" v-if="form.carry_forward">
                  <label class="form-label dq-label">Maximum Carry Forward Days</label>
                  <input
                    v-model.number="form.carry_forward_max"
                    type="number"
                    min="0"
                    class="form-control dq-input"
                    placeholder="e.g., 7"
                  />
                </div>
              </div>
            </div>

            <!-- Status -->
            <div class="dq-section mt-3">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-badge dq-badge-slate">
                    <i class="ti ti-toggle-left"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Availability</div>
                    <div class="dq-help">Control whether employees can select this leave type.</div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="dq-switch-card">
                  <div class="dq-switch-title">
                    <i class="ti ti-circle-check me-1"></i> Enabled
                  </div>
                  <div class="form-check form-switch dq-switch">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      v-model="form.is_enabled"
                      id="dq_enabled_switch"
                    />
                    <label class="form-check-label" for="dq_enabled_switch">
                      {{ form.is_enabled ? "Enabled" : "Disabled" }}
                    </label>
                  </div>
                  <div class="dq-help">
                    Disabled leave types remain in records but won’t be selectable for new requests.
                  </div>
                </div>
              </div>
            </div>

            <div class="dq-section-body mt-3">
              <div class="dq-switch-card">
                <div class="dq-switch-title">
                  <i class="ti ti-layout-list me-1"></i> Include In "Other Leaves"
                </div>
                <div class="form-check form-switch dq-switch">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="form.count_in_other"
                    id="dq_count_other_switch"
                  />
                  <label class="form-check-label" for="dq_count_other_switch">
                    {{ form.count_in_other ? "Include" : "Exclude" }}
                  </label>
                </div>
              </div>
            </div>

            <!-- Tiny warning for custom name + missing name -->
            <div
              v-if="nameSelection === '__custom__' && !form.name"
              class="alert alert-warning dq-alert mt-3"
            >
              <i class="ti ti-alert-triangle me-1"></i>
              Custom name is required.
            </div>
          </div>

          <div class="modal-footer dq-footer">
            <button
              type="button"
              class="btn btn-white border dq-btn dq-btn-muted me-2"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>

            <button type="submit" class="btn btn-primary dq-btn dq-btn-primary" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
              {{ isEdit ? "Save Changes" : "Create Leave Type" }}
              <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Delete Modal -->
  <div class="modal fade dq-leave-type-modal" id="delete_modal">
    <div class="modal-dialog modal-dialog-centered modal-sm dq-leave-type-delete-dialog">
      <div class="modal-content dq-modal-content">
        <div class="modal-body text-center dq-delete-body">
          <span class="avatar avatar-xl bg-transparent-danger text-danger mb-3 dq-danger-avatar">
            <i class="ti ti-trash-x fs-36"></i>
          </span>
          <h4 class="mb-1 dq-delete-title">Confirm Delete</h4>
          <p class="mb-3 dq-delete-sub">Are you sure you want to delete this record?</p>

          <div class="d-flex justify-content-center">
            <a href="javascript:void(0);" class="btn btn-light me-3 dq-btn dq-btn-muted" data-bs-dismiss="modal">
              Cancel
            </a>
            <button class="btn btn-danger dq-btn dq-btn-danger" :disabled="deleting" @click="confirmDelete">
              <span v-if="deleting" class="spinner-border spinner-border-sm me-2"></span>
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from "vue";
import { useLeaveTypeStore } from "@/stores/leaveTypeStore";
import leaveTypeService from "@/services/hrm/leaveTypeService";
import { toast } from "@/utils/toast";

const props = defineProps({ editItem: { type: Object, default: null } });
const emit = defineEmits(["saved"]);

const store = useLeaveTypeStore();
const saving = computed(() => store.loading);
const modalKey = ref(0);
const deleting = ref(false);

/** Local suggestions (no fetch) + sensible defaults */
const suggestions = ref([
  { name: "Annual Leave", code: "ANN", default_days: 21, paid: true, carry_forward: true, carry_forward_max: 7, eligibility: "all", visible_in_employee: true, count_in_other: false },
  { name: "Sick Leave", code: "SICK", default_days: 30, paid: true, carry_forward: false, carry_forward_max: null, eligibility: "all", visible_in_employee: true, count_in_other: false },
  { name: "Maternity Leave", code: "MAT", default_days: 90, paid: true, carry_forward: false, carry_forward_max: null, eligibility: "female_only", visible_in_employee: true, count_in_other: false },
  { name: "Paternity Leave", code: "PAT", default_days: 14, paid: true, carry_forward: false, carry_forward_max: null, eligibility: "male_only", visible_in_employee: true, count_in_other: false },
  { name: "Compassionate Leave", code: "COMP", default_days: 10, paid: true, carry_forward: false, carry_forward_max: null, eligibility: "all", visible_in_employee: true, count_in_other: false },
]);

const form = ref(blank());
const nameSelection = ref("");

function blank() {
  return {
    name: "",
    code: "",
    default_days: 0,
    is_enabled: true,
    carry_forward: false,
    carry_forward_max: null,
    paid: true,
    eligibility: "all",
    visible_in_employee: true,
    count_in_other: true,
  };
}

function reset() {
  const b = blank();
  for (const k in b) form.value[k] = b[k];
  nameSelection.value = "";
}

/** Handle name selection from dropdown */
function onNameSelect() {
  if (nameSelection.value === "__custom__") {
    form.value.name = "";
    form.value.code = "";
  } else if (nameSelection.value) {
    const s = suggestions.value.find((x) => x.name === nameSelection.value);
    if (s) {
      form.value.name = s.name;
      form.value.code = s.code;
      form.value.default_days = s.default_days;
      form.value.paid = s.paid;
      form.value.carry_forward = s.carry_forward;
      form.value.carry_forward_max = s.carry_forward ? s.carry_forward_max : null;
      form.value.eligibility = s.eligibility || "all";
      form.value.visible_in_employee = s.visible_in_employee ?? true;
      form.value.count_in_other = s.count_in_other ?? true;
    }
  } else {
    form.value.name = "";
    form.value.code = "";
  }
}

watch(
  () => props.editItem,
  (val) => {
    if (val) {
      form.value.name = val.name ?? "";
      form.value.code = val.code ?? "";
      form.value.default_days = Number(val.default_days ?? 0);
      form.value.is_enabled = !!val.is_enabled;
      form.value.carry_forward = !!val.carry_forward;
      form.value.carry_forward_max = val.carry_forward ? Number(val.carry_forward_max ?? 0) : null;
      form.value.paid = !!val.paid;
      form.value.eligibility = val.eligibility || "all";
      form.value.visible_in_employee = val.visible_in_employee ?? true;
      form.value.count_in_other = val.count_in_other ?? true;

      const matchingSuggestion = suggestions.value.find((s) => s.name === val.name);
      nameSelection.value = matchingSuggestion ? val.name : "__custom__";

      modalKey.value++;
    } else {
      reset();
    }
  },
  { immediate: true }
);

const isEdit = computed(() => !!props.editItem?.id);

async function submit() {
  if (!form.value.name || !form.value.code) {
    toast("Name and code are required", "error");
    return;
  }
  const payload = { ...form.value };

  if (isEdit.value) {
    await store.update(props.editItem.id, payload);
  } else {
    await store.create(payload);
  }
  close();
  modalKey.value++;
  emit("saved");
}

function close() {
  document.querySelector('#leave_type_modal [data-bs-dismiss="modal"]')?.click();
}

async function confirmDelete() {
  deleting.value = true;
  try {
    if (!props.editItem?.id) return;
    await store.remove(props.editItem.id);
    document.querySelector('#delete_modal [data-bs-dismiss="modal"]')?.click();
    emit("saved");
  } catch (e) {
    console.error(e);
  } finally {
    deleting.value = false;
  }
}

let cleanup = null;
onMounted(() => {
  leaveTypeService
    .getTemplates()
    .then(({ data }) => {
      if (Array.isArray(data?.data) && data.data.length) {
        suggestions.value = data.data;
      }
    })
    .catch(() => {});

  const addEl = document.getElementById("leave_type_modal");
  const handleAddShow = () => {
    if (!props.editItem?.id) reset();
    modalKey.value++;
  };
  const handleAddHidden = () => reset();

  addEl?.addEventListener("show.bs.modal", handleAddShow);
  addEl?.addEventListener("hidden.bs.modal", handleAddHidden);

  cleanup = () => {
    addEl?.removeEventListener("show.bs.modal", handleAddShow);
    addEl?.removeEventListener("hidden.bs.modal", handleAddHidden);
  };
});

onBeforeUnmount(() => cleanup && cleanup());
</script>

<style scoped>
/* ===============================
   WIDTH / LAYOUT
================================ */
.dq-leave-type-dialog {
  max-width: 820px; /* wider, premium */
}
@media (max-width: 768px) {
  .dq-leave-type-dialog {
    max-width: 96%;
  }
}
.dq-leave-type-delete-dialog {
  max-width: 520px;
}

/* ===============================
   MODAL SHELL
================================ */
.dq-modal-content {
  border-radius: 22px;
  border: none;
  overflow: hidden;
  box-shadow:
    0 10px 25px rgba(15, 23, 42, 0.08),
    0 40px 80px rgba(15, 23, 42, 0.12);
}

/* ===============================
   HEADER
================================ */
.dq-modal-header {
  padding: 18px 22px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, rgba(99, 102, 241, 0.10), #fff);
}

.dq-titlewrap {
  display: flex;
  align-items: center;
  gap: 12px;
}

.dq-title {
  font-weight: 900;
  letter-spacing: -0.35px;
}

.dq-subtitle {
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}

.dq-close {
  opacity: 0.85;
}
.dq-close:hover {
  opacity: 1;
}

/* Hero */
.dq-hero {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}
.dq-hero-indigo {
  background: rgba(99, 102, 241, 0.16);
  color: rgb(79, 70, 229);
}

/* ===============================
   BODY / SECTIONS
================================ */
.dq-body {
  padding: 18px 22px;
}

.dq-section {
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 18px;
  background: rgba(15, 23, 42, 0.02);
  overflow: hidden;
}

.dq-section-head {
  padding: 14px 14px 0 14px;
}

.dq-section-body {
  padding: 14px;
}

.dq-section-title {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.dq-section-badge {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex: 0 0 auto;
  background: rgba(15, 23, 42, 0.05);
}

.dq-badge-indigo {
  background: rgba(99, 102, 241, 0.12);
  color: rgb(79, 70, 229);
}
.dq-badge-blue {
  background: rgba(59, 130, 246, 0.12);
  color: rgb(37, 99, 235);
}
.dq-badge-green {
  background: rgba(34, 197, 94, 0.12);
  color: rgb(22, 163, 74);
}
.dq-badge-slate {
  background: rgba(15, 23, 42, 0.06);
  color: rgba(15, 23, 42, 0.75);
}

.dq-h6 {
  font-weight: 900;
  letter-spacing: -0.2px;
}

.dq-help {
  font-size: 0.8rem;
  color: rgba(15, 23, 42, 0.55);
  line-height: 1.35;
}

.dq-label {
  font-size: 0.82rem;
  font-weight: 800;
}

/* ===============================
   INPUTS
================================ */
.dq-input {
  border-radius: 14px;
  padding: 10px 12px;
  transition: all 0.15s ease;
}

.dq-input:focus {
  border-color: rgba(59, 130, 246, 0.55);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.14);
}

.dq-input-group .dq-prefix {
  border-radius: 14px 0 0 14px;
  background: rgba(15, 23, 42, 0.03);
  border-color: rgba(15, 23, 42, 0.08);
}

/* ===============================
   SWITCH CARDS
================================ */
.dq-switch-card {
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 16px;
  background: #fff;
  padding: 12px 12px;
}

.dq-switch-card-soft {
  background: rgba(34, 197, 94, 0.05);
}

.dq-switch-title {
  font-weight: 900;
  font-size: 0.9rem;
  color: rgba(15, 23, 42, 0.85);
  margin-bottom: 6px;
}

.dq-switch {
  margin-bottom: 6px;
}

/* ===============================
   ALERT
================================ */
.dq-alert {
  border-radius: 14px;
}

/* ===============================
   FOOTER / BUTTONS
================================ */
.dq-footer {
  padding: 14px 22px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
}

.dq-btn {
  border-radius: 12px;
  padding: 10px 16px;
  font-weight: 800;
}

.dq-btn-muted {
  font-weight: 700;
}

.dq-btn-primary {
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.08);
}

/* ===============================
   DELETE MODAL POLISH
================================ */
.dq-delete-body {
  padding: 22px;
}
.dq-danger-avatar {
  border-radius: 18px;
}
.dq-delete-title {
  font-weight: 900;
}
.dq-delete-sub {
  color: rgba(15, 23, 42, 0.6);
}
.dq-btn-danger {
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.08);
}
</style>
